<template>
  <div
    class="notification-item"
    :class="{ 'notification-item--unread': !notification.isRead }"
    @click="handleClick"
  >
    <div class="notification-item__indicator">
      <div v-if="!notification.isRead" class="notification-item__dot" />
    </div>

    <div class="notification-item__icon">
      <component :is="iconComponent" :size="20" />
    </div>

    <div class="notification-item__content">
      <div class="notification-item__title">{{ notification.title }}</div>
      <div class="notification-item__body">{{ notification.body }}</div>
      <!-- DEV-1946: видимая ссылка. Переход по уведомлению существовал с DEV-1005
           (клик по всему блоку), но выглядел как обычный текст — что уведомление
           куда-то ведёт, по нему было не видно. Логика перехода та же. -->
      <button
        v-if="linkLabel"
        type="button"
        class="notification-item__open"
        @click.stop="handleClick"
      >
        {{ linkLabel }}
        <ArrowRightIcon :size="12" />
      </button>

      <div class="notification-item__meta">
        <span class="notification-item__time">{{ formattedTime }}</span>
        <span v-if="notification.channelsSent.length" class="notification-item__channels">
          <span
            v-for="ch in notification.channelsSent"
            :key="ch"
            class="notification-item__channel-badge"
          >
            {{ ch }}
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  MessageSquareIcon,
  BellIcon,
  CalendarIcon,
  UsersIcon,
  BriefcaseIcon,
  CopyIcon,
  ArrowRightIcon,
  PhoneIcon,
  HandshakeIcon,
  SparklesIcon,
} from 'lucide-vue-next'
import { buildNotificationRoute } from '../utils/notificationRoute'
import type { Notification } from '../types/notifications.types'

const props = defineProps<{
  notification: Notification
}>()

const emit = defineEmits<{
  click: [notification: Notification]
}>()

const { t } = useI18n()

const iconComponent = computed(() => {
  switch (props.notification.notificationType) {
    case 'unread_message':
    // DEV-2804: клиент ждёт менеджера в чате после ассистента.
    case 'messenger_bot_handoff':
      return MessageSquareIcon
    // DEV-2803: в мессенджер написал агент по недвижимости (ссылка — вкладка «Агенты»).
    case 'messenger_agent_contact':
      return HandshakeIcon
    // DEV-2808: ИИ предложил правки карточки по переписке (ссылка — карточка клиента).
    case 'chat_qualification':
      return SparklesIcon
    case 'calendar_reminder':
      return CalendarIcon
    case 'client_update':
      return UsersIcon
    case 'deal_update':
      return BriefcaseIcon
    case 'agent_lead_duplicate':
      return CopyIcon
    case 'incoming_call':
    case 'missed_call':
      return PhoneIcon
    default:
      return BellIcon
  }
})

// Подпись ссылки зависит от того, куда уведомление ведёт: карточка клиента у
// дублей и обновлений клиента, всё остальное — нейтральное «Открыть».
const linkLabel = computed(() => {
  if (!buildNotificationRoute(props.notification)) return ''

  return props.notification.metadata?.client_id
    ? t('notifications.link.openClient')
    : t('notifications.link.open')
})

const formattedTime = computed(() => {
  if (!props.notification.createdAt) return ''
  const date = new Date(props.notification.createdAt)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMin < 1) return t('notifications.time.justNow')
  if (diffMin < 60) return t('notifications.time.minutesAgo', { n: diffMin })
  if (diffHours < 24) return t('notifications.time.hoursAgo', { n: diffHours })
  if (diffDays < 7) return t('notifications.time.daysAgo', { n: diffDays })

  return date.toLocaleDateString()
})

function handleClick() {
  emit('click', props.notification)
}
</script>

<style scoped>
.notification-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm, 0.5rem);
  padding: var(--spacing-sm, 0.75rem) var(--spacing-md, 1rem);
  cursor: pointer;
  transition: background-color 0.15s;
  border-bottom: 1px solid var(--color-border-light);
}

.notification-item:hover {
  background: var(--color-background-secondary);
}

.notification-item--unread {
  background: var(--color-primary-alpha-10);
}

.notification-item--unread:hover {
  background: var(--color-primary-alpha-20);
}

.notification-item__open {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  margin-top: var(--spacing-xs, 0.25rem);
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: var(--font-size-sm, 0.8125rem);
  font-weight: 500;
  color: var(--color-primary);
  cursor: pointer;
}

.notification-item__open:hover {
  text-decoration: underline;
}

.notification-item__indicator {
  width: 8px;
  flex-shrink: 0;
  padding-top: 6px;
}

.notification-item__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-primary);
}

.notification-item__icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md, 0.5rem);
  background: var(--color-background-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
}

.notification-item--unread .notification-item__icon {
  background: var(--color-primary-light, rgba(59, 130, 246, 0.1));
  color: var(--color-primary);
}

.notification-item__content {
  flex: 1;
  min-width: 0;
}

.notification-item__title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.notification-item__body {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  line-height: 1.4;
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-item__meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 0.5rem);
  margin-top: 4px;
}

.notification-item__time {
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
}

.notification-item__channels {
  display: flex;
  gap: 4px;
}

.notification-item__channel-badge {
  font-size: 0.625rem;
  padding: 1px 6px;
  border-radius: var(--radius-sm, 0.25rem);
  background: var(--color-background-secondary);
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* DEV-2248: в узкой панели две строки обрывались прямо на сути уведомления
   («назначен на РОПа Claude ROP: нет менеджеров с…») — на телефоне даём четыре. */
@media (max-width: 640px) {
  .notification-item__body {
    -webkit-line-clamp: 4;
  }
}
</style>
